/**
 * Static reward (出局倍数) — principal snapshot + exit multipliers.
 */
import type { TokenId } from './tokens.js';
import { LEDGER_ACTION } from './enums.js';

export type StarPackage = 'E1' | 'E2' | 'E3';
export type ReinvestCoinType = 'sp1' | 'sp2' | 'sp3';
/** How the account was paid at register / 开子. */
export type RegisterPayCoin = ReinvestCoinType | 'rp';
/** Node 新系统注册 vs 旧 PHP 会员跟到新系统. */
export type AccountOrigin = 'new_system' | 'legacy';

export interface StaticExitRule {
  principal_usd: string;
  exit_multiplier: string;
}

export const MAIN_STAR_PRINCIPAL_USD: Record<StarPackage, string> = {
  E1: '100',
  E2: '1000',
  E3: '10000',
};

/** Binary perf / 对碰累计口径 = 配套奖励值（≠ STATIC_PAYOUT_BASE_USD 静态 gross）。 */
export const BINARY_PERF_REWARD_VALUE_USD = MAIN_STAR_PRINCIPAL_USD;

/** One static payout tranche (register queue_ap / SPx 复投开子) — half of package USD. */
export const STATIC_PAYOUT_BASE_USD: Record<StarPackage, string> = {
  E1: '50',
  E2: '500',
  E3: '5000',
};

export function resolveStaticPayoutBaseUsd(starPackage: StarPackage): string {
  return STATIC_PAYOUT_BASE_USD[starPackage] ?? STATIC_PAYOUT_BASE_USD.E2;
}

/** Fallback when pay-coin is unknown (主号开通 SP2+TP, not 注册币种). */
export const MAIN_EXIT_MULTIPLIER: Record<string, string> = {
  E1: '3',
  E2: '4',
  E3: '4',
};

/** 100 原点升 1000：基数改 1000，倍数仍 3 → 出局 3000（不走 E2 新开 4 倍）。 */
export const E1_TO_E2_UPGRADE_EXIT_MULTIPLIER = '3';

/**
 * E1 一星注册/开子：除旧 SP2=1.2 外一律 3 倍（出局 300）。
 * 含新系统 RP/SP3、旧系统 SP1/RP/SP3。E2/E3 仍走下方币种表。
 */
export const E1_REGISTER_EXIT_MULTIPLIER = '3';

/**
 * 新系统 E2/E3 注册：SP1 → 3 倍；RP / SP3 → 4 倍。
 * 新系统不开 SP2 注册；若传入 sp2 仍按 1.2（与旧系统一致，避免误开成 4 倍）。
 */
export const NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER: Record<RegisterPayCoin, string> = {
  sp1: '3',
  rp: '4',
  sp3: '4',
  sp2: '1.2',
};

/**
 * 旧会员系统跟到新系统（E2/E3）：SP2 → 1.2 倍；SP1 / RP / SP3 → 4 倍。
 */
export const LEGACY_REGISTER_EXIT_MULTIPLIER: Record<RegisterPayCoin, string> = {
  sp1: '4',
  rp: '4',
  sp3: '4',
  sp2: '1.2',
};

/** @deprecated use NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER — kept for admin registry default. */
export const SUB_REINVEST_EXIT_MULTIPLIER: Record<ReinvestCoinType, string> = {
  sp1: NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER.sp1,
  sp2: LEGACY_REGISTER_EXIT_MULTIPLIER.sp2,
  sp3: NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER.sp3,
};

/** Ledger actions that count toward static exit cap (excludes daily_settlement). */
export const STATIC_REWARD_ACTIONS = [
  LEDGER_ACTION.STATIC_REWARD_EP1,
  LEDGER_ACTION.STATIC_REWARD_SP1,
  LEDGER_ACTION.STATIC_REWARD_EP2,
  LEDGER_ACTION.STATIC_REWARD_SP2,
  LEDGER_ACTION.STATIC_REWARD_RP,
  LEDGER_ACTION.STATIC_REWARD_FC,
] as const;

/** Static payout legs only — EP1 + SP1 (excludes RP/FC and dynamic EP2/SP2). */
export const STATIC_EP1_SP1_ACTIONS = [
  LEDGER_ACTION.STATIC_REWARD_EP1,
  LEDGER_ACTION.STATIC_REWARD_SP1,
] as const;

export const STATIC_PAYOUT_SPLIT: ReadonlyArray<{
  asset: TokenId;
  pct: string;
  action: (typeof LEDGER_ACTION)[keyof typeof LEDGER_ACTION];
}> = [
  { asset: 'ep1', pct: '0.52', action: LEDGER_ACTION.STATIC_REWARD_EP1 },
  { asset: 'sp1', pct: '0.36', action: LEDGER_ACTION.STATIC_REWARD_SP1 },
  { asset: 'rp', pct: '0.10', action: LEDGER_ACTION.STATIC_REWARD_RP },
  { asset: 'fc', pct: '0.02', action: LEDGER_ACTION.STATIC_REWARD_FC },
];

export const WITHDRAW_SPLIT_STATIC: ReadonlyArray<{
  asset: TokenId;
  pct: string;
  leg: 'credit' | 'fee' | 'burn';
}> = [
  { asset: 'ep1', pct: '0.52', leg: 'credit' },
  { asset: 'sp1', pct: '0.36', leg: 'credit' },
  { asset: 'tp', pct: '0.10', leg: 'fee' },
  { asset: 'fc', pct: '0.02', leg: 'burn' },
];

/** Dynamic awards / dynamic withdraw track: 100% EP2 (no SP2/TP/FC split). */
export const WITHDRAW_SPLIT_DYNAMIC: ReadonlyArray<{
  asset: TokenId;
  pct: string;
  leg: 'credit' | 'fee' | 'burn';
}> = [
  { asset: 'ep2', pct: '1', leg: 'credit' },
];

export function resolveMainExitRule(levelCode: string, starPackage: StarPackage): StaticExitRule {
  const principal = MAIN_STAR_PRINCIPAL_USD[starPackage] ?? MAIN_STAR_PRINCIPAL_USD.E1;
  const mult = MAIN_EXIT_MULTIPLIER[levelCode] ?? MAIN_EXIT_MULTIPLIER.E1;
  return { principal_usd: principal, exit_multiplier: mult };
}

/** uo_config keys — admin 奖励分发配置. Extra JSON keys are allowed (new coins / packages). */
export const STATIC_EXIT_CONFIG_KEYS = {
  MAIN_PRINCIPAL: 'reward.static.main_principal_usd',
  MAIN_EXIT: 'reward.static.main_exit_multiplier',
  NEW_SYSTEM_REGISTER: 'reward.static.new_system_register_exit',
  LEGACY_REGISTER: 'reward.static.legacy_register_exit',
  /** @deprecated split into NEW_SYSTEM_REGISTER / LEGACY_REGISTER */
  SUB_EXIT: 'reward.static.sub_exit_multiplier',
} as const;

export function resolveRegisterExitMultiplier(
  origin: AccountOrigin,
  payCoin: string,
  starPackage?: StarPackage,
): string {
  const key = payCoin.trim().toLowerCase();
  if (starPackage === 'E1' && key !== 'sp2') {
    return E1_REGISTER_EXIT_MULTIPLIER;
  }
  const table =
    origin === 'legacy' ? LEGACY_REGISTER_EXIT_MULTIPLIER : NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER;
  if (key === 'sp1' || key === 'sp2' || key === 'sp3' || key === 'rp') {
    return table[key];
  }
  return table.rp;
}

export function resolveStaticExitRule(opts: {
  starPackage: StarPackage;
  levelCode?: string;
  origin?: AccountOrigin;
  payCoin?: string;
}): StaticExitRule {
  const principal = MAIN_STAR_PRINCIPAL_USD[opts.starPackage] ?? MAIN_STAR_PRINCIPAL_USD.E1;
  if (opts.payCoin) {
    return {
      principal_usd: principal,
      exit_multiplier: resolveRegisterExitMultiplier(
        opts.origin ?? 'new_system',
        opts.payCoin,
        opts.starPackage,
      ),
    };
  }
  const levelCode = opts.levelCode ?? opts.starPackage;
  return resolveMainExitRule(levelCode, opts.starPackage);
}

export function resolveE1ToE2UpgradeExitRule(): StaticExitRule {
  return {
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E2,
    exit_multiplier: E1_TO_E2_UPGRADE_EXIT_MULTIPLIER,
  };
}

export function resolveSubExitRule(
  starPackage: StarPackage,
  reinvestCoinType: ReinvestCoinType,
  origin: AccountOrigin = 'new_system',
): StaticExitRule {
  return resolveStaticExitRule({
    starPackage,
    origin,
    payCoin: reinvestCoinType,
  });
}
